import { defineStore } from "pinia";
import { v4 as uuid } from "uuid";
import { computed, ref } from "vue";

import { $t } from "@/i18n";

/**
 * AI record creation mode picked in the upload dialog (#3146). `perFile` creates one AI record per
 * selected file; `fromSelection` creates a single record out of all selected files, which needs the
 * multi-file classification API from #3145 — until that lands the queue falls back to `perFile`
 * behaviour and reports the requested mode unchanged.
 */
export type AiRecordMode = "perFile" | "fromSelection";

export type UploadItemStatus = "pending" | "uploading" | "uploaded" | "failed";

/**
 * AI state of one queued file. `none` means the file was uploaded without an AI request at all;
 * everything else tracks the classification pipeline: `queued` (upload done, classify call not
 * answered yet), `processing` (workflow running, step events arriving), then one terminal value.
 */
export type AiItemStatus = "none" | "queued" | "processing" | "done" | "failed" | "duplicate";

export type AiTerminalStatus = Extract<AiItemStatus, "done" | "failed" | "duplicate">;

const AI_TERMINAL_STATUSES: AiItemStatus[] = ["done", "failed", "duplicate"];

export interface UploadQueueItem {
  id: string;
  /** Upload run this file belongs to — echoed by every classification event of that run. */
  batchId: string;
  /** The blob to upload. Kept on the item so a run never has to re-align items with a file array. */
  file: File;
  name: string;
  size: number;
  aiRequested: boolean;
  status: UploadItemStatus;
  bytesUploaded: number;
  errorMessage?: string;
  /** Storage key of the uploaded blob, known once the upload finished. */
  objectName?: string;
  /** Resolved once the classify call for this run was accepted. */
  fileId?: string;
  /** The file's own Files-module record — the general record-fields push is keyed by it. */
  fileRecordId?: string;
  aiStatus: AiItemStatus;
  /** Last reported pipeline step for this file, 1-based (see backend fileToRecordAIStepTotal). */
  aiStep: number;
  aiStepTotal: number;
}

/** One staged file plus whether the user marked it for AI record generation. */
export interface UploadQueueEntry {
  file: File;
  aiRequested: boolean;
}

export interface UploadRunResult {
  /** Storage key of the uploaded blob. Absent for uploads that never go through classification. */
  objectName?: string;
}

/**
 * Starts classification for the files of one run and reports which file id each object name got.
 * One call for the whole selection, because `fromSelection` turns it into a single record (#3145).
 */
export type Classifier = (
  objectNames: string[],
  mode: AiRecordMode,
) => Promise<{ objectName: string; fileId: string; fileRecordId?: string }[]>;

/**
 * Uploads one file and, when the item asked for it, triggers classification. Injected by the caller
 * (the upload dialog) instead of created here so the store stays free of Vue-injection-bound
 * composables and remains unit-testable.
 */
export type UploadRunner = (
  item: UploadQueueItem,
  onProgress: (bytesUploaded: number) => void,
) => Promise<UploadRunResult | void>;

/**
 * How long classification may go completely silent — no step, no outcome — before the queue gives up
 * on the files still in flight.
 *
 * Generous on purpose. Silence is normal for minutes at a time: the first step is only published once
 * every file of the run has been fetched, converted and uploaded to the provider, and the gaps after
 * that span whole model calls (seconds to tens of seconds each, retried on provider throttling). This
 * exists solely so a dead worker or a lost event stream cannot leave the popup up forever, so it must
 * sit well above any legitimate gap.
 */
const AI_SILENCE_TIMEOUT_MS = 15 * 60 * 1000;

function isTerminalAi(status: AiItemStatus) {
  return AI_TERMINAL_STATUSES.includes(status);
}

/**
 * Holds the file upload runs of the Files module upload dialog, so progress survives the dialog
 * closing: the dialog only enqueues and starts a run, the progress popups read this store (#3146).
 *
 * Several runs can be in flight at once — each gets its own `batchId`, which travels to the backend
 * with the classify call and comes back on every classification event, so a push is always matched
 * to the run that started it. The popups aggregate across active runs, matching the single-popup
 * design.
 *
 * Deliberately not persisted — an in-flight tus upload cannot be resumed across a page reload, so a
 * restored queue would only show stale counters.
 */
export const useFileUploadQueue = defineStore("fileUploadQueue", () => {
  const items = ref<UploadQueueItem[]>([]);
  /** Requested AI mode per run. Kept per batch because runs can overlap. */
  const modes = ref<Record<string, AiRecordMode>>({});
  const runningBatches = ref<string[]>([]);
  const now = ref(Date.now());
  /** When the last classification event arrived, for the silence watchdog below. */
  let lastAiEventAt = Date.now();
  let ticker: ReturnType<typeof setInterval> | undefined;

  const isRunning = computed(() => runningBatches.value.length > 0);

  const uploadTotal = computed(() => items.value.length);
  const uploadedCount = computed(() => items.value.filter((item) => item.status === "uploaded").length);
  const failedCount = computed(() => items.value.filter((item) => item.status === "failed").length);
  const settledCount = computed(() => uploadedCount.value + failedCount.value);
  const bytesTotal = computed(() => items.value.reduce((sum, item) => sum + item.size, 0));
  const bytesUploaded = computed(() => items.value.reduce((sum, item) => sum + item.bytesUploaded, 0));

  /**
   * Share of transferred bytes, not of finished files: a single-file run — the common case for a
   * folder upload — would otherwise sit at 0% and then snap straight to 100%.
   */
  const uploadPercentage = computed(() =>
    bytesTotal.value === 0 ? 0 : Math.min(100, Math.round((bytesUploaded.value / bytesTotal.value) * 100)),
  );

  const isUploadFinished = computed(() => uploadTotal.value > 0 && settledCount.value === uploadTotal.value);

  /**
   * The whole run is done handing work to the backend: every upload settled AND the classify call
   * returned. Consumers that reload a list must wait for this, not for isUploadFinished — classify
   * flips each file's record to ai_processing one file at a time, so a reload started at upload-finish
   * can read the list while those writes are still in flight and end up showing only some of the
   * files, with nothing to backfill the rest later (MR !2839).
   */
  const isBatchSettled = computed(() => isUploadFinished.value && !isRunning.value);

  const aiItems = computed(() => items.value.filter((item) => item.aiRequested));
  const aiTotal = computed(() => aiItems.value.length);
  const aiProcessedCount = computed(() => aiItems.value.filter((item) => isTerminalAi(item.aiStatus)).length);
  const isAiFinished = computed(() => aiTotal.value > 0 && aiProcessedCount.value === aiTotal.value);

  /**
   * Sum of each AI file's own step ratio — 0..aiTotal, fractional while a file is mid-pipeline. A
   * terminal file counts as a full one regardless of the last step seen — the duplicate and
   * low-confidence exits stop early on purpose.
   */
  const aiProgressUnits = computed(() =>
    aiItems.value.reduce((sum, item) => {
      if (isTerminalAi(item.aiStatus)) return sum + 1;
      if (item.aiStepTotal <= 0) return sum;
      return sum + Math.min(1, item.aiStep / item.aiStepTotal);
    }, 0),
  );

  /** Averages each file's own step ratio so the bar moves inside a file too, instead of only jumping
   * per finished file. */
  const aiPercentage = computed(() => {
    if (aiTotal.value === 0) return 0;
    return Math.min(100, Math.round((aiProgressUnits.value / aiTotal.value) * 100));
  });

  const hasActiveUpload = computed(() => uploadTotal.value > 0 && !isUploadFinished.value);
  const hasActiveAi = computed(() => aiTotal.value > 0 && !isAiFinished.value);

  function startTicker() {
    if (ticker) return;
    now.value = Date.now();
    lastAiEventAt = Date.now();
    ticker = setInterval(() => {
      now.value = Date.now();
      failSilentAiItems();
    }, 1000);
  }

  /**
   * Marks the files still waiting on classification as failed once nothing has been heard for
   * AI_SILENCE_TIMEOUT_MS. Their status is genuinely unknown at that point, but a stuck progress
   * popup is worse than an honest failure the user can retry from.
   */
  function failSilentAiItems() {
    if (!hasActiveAi.value || isRunning.value) return;
    if (now.value - lastAiEventAt < AI_SILENCE_TIMEOUT_MS) return;

    aiItems.value.forEach((item) => {
      if (isTerminalAi(item.aiStatus)) return;
      item.aiStatus = "failed";
      item.errorMessage ??= $t("fileExplorer.upload.classificationStalled");
    });
    stopTicker();
  }

  function stopTicker() {
    if (!ticker) return;
    clearInterval(ticker);
    ticker = undefined;
  }

  /**
   * Stops the 1s clock only when nothing needs it any more. Classification finishing does not mean
   * the queue is idle: another run may still be uploading.
   */
  function stopTickerWhenIdle() {
    if (isAiFinished.value && !hasActiveUpload.value && !isRunning.value) stopTicker();
  }

  /**
   * Registers one upload run and returns its id. Entries carry their own `aiRequested` flag rather
   * than a list of file names — two files can share a name while only one of them is marked for AI.
   */
  function enqueue(entries: UploadQueueEntry[], options: { mode?: AiRecordMode } = {}): string {
    const batchId = uuid();
    modes.value[batchId] = options.mode ?? "perFile";

    items.value = [
      ...items.value,
      ...entries.map<UploadQueueItem>((entry) => ({
        id: uuid(),
        batchId,
        file: entry.file,
        name: entry.file.name,
        size: entry.file.size,
        aiRequested: entry.aiRequested,
        status: "pending",
        bytesUploaded: 0,
        aiStatus: entry.aiRequested ? "queued" : "none",
        aiStep: 0,
        aiStepTotal: 0,
      })),
    ];

    return batchId;
  }

  function modeOf(batchId: string): AiRecordMode {
    return modes.value[batchId] ?? "perFile";
  }

  /**
   * Runs the uploads of one batch. Files go out in parallel (unchanged from the previous dialog
   * behaviour) and a single failure never stops the rest — each item carries its own status.
   */
  async function run(batchId: string, runner: UploadRunner, classifier?: Classifier) {
    const batchItems = items.value.filter((item) => item.batchId === batchId);
    if (runningBatches.value.includes(batchId) || batchItems.length === 0) return;

    runningBatches.value = [...runningBatches.value, batchId];
    startTicker();

    try {
      await Promise.allSettled(
        batchItems.map(async (item) => {
          item.status = "uploading";
          try {
            const result = await runner(item, (uploaded) => {
              item.bytesUploaded = Math.min(item.size, uploaded);
            });
            item.status = "uploaded";
            item.bytesUploaded = item.size;
            item.objectName = result?.objectName;
          } catch (error) {
            item.status = "failed";
            item.errorMessage = error instanceof Error ? error.message : String(error);
            if (item.aiRequested) {
              item.aiStatus = "failed";
            }
          }
        }),
      );

      // Classification runs once for the whole run, after every upload settled: in `fromSelection`
      // the backend needs the complete selection to build one record out of it.
      await classifyBatch(batchId, classifier);
    } finally {
      runningBatches.value = runningBatches.value.filter((id) => id !== batchId);
      if (!hasActiveAi.value && !isRunning.value) stopTicker();
    }
  }

  async function classifyBatch(batchId: string, classifier?: Classifier) {
    const pending = items.value.filter(
      (item) => item.batchId === batchId && item.aiRequested && item.status === "uploaded" && item.objectName,
    );
    if (pending.length === 0) return;

    if (!classifier) {
      pending.forEach((item) => {
        item.aiStatus = "failed";
      });
      return;
    }

    try {
      const started = await classifier(
        pending.map((item) => item.objectName!),
        modeOf(batchId),
      );
      const byObjectName = new Map(started.map((entry) => [entry.objectName, entry]));

      // The watchdog's clock starts here, not when the upload did: everything before this point is
      // transfer time, and counting it made a long upload look like a silent classification.
      lastAiEventAt = Date.now();

      pending.forEach((item) => {
        const entry = byObjectName.get(item.objectName!);
        item.fileId = entry?.fileId;
        item.fileRecordId = entry?.fileRecordId;
        // No file id means the run never started for this file, so nothing will ever settle it.
        item.aiStatus = entry?.fileId ? "processing" : "failed";
      });
    } catch (error) {
      pending.forEach((item) => {
        item.aiStatus = "failed";
        item.errorMessage ??= error instanceof Error ? error.message : String(error);
      });
    }
  }

  /**
   * Finds the queued file an event refers to. `batchId` narrows the search to the run that started
   * the classification, so a push from another run — or another browser tab — is ignored instead of
   * moving the wrong bar. Events from a backend that does not echo the id still match by file.
   */
  function itemForEvent(fileId: string, batchId?: string) {
    return items.value.find((item) => item.fileId === fileId && (!batchId || item.batchId === batchId));
  }

  /** Applies one `file_classification.progress` push. Unknown files are ignored. */
  function applyAiProgress(payload: { fileId: string; batchId?: string; stepIndex: number; stepTotal: number }) {
    const item = itemForEvent(payload.fileId, payload.batchId);
    if (!item || isTerminalAi(item.aiStatus)) return;

    lastAiEventAt = Date.now();
    item.aiStatus = "processing";
    item.aiStep = Math.max(item.aiStep, payload.stepIndex);
    item.aiStepTotal = payload.stepTotal;
  }

  /** Applies a `file_classification.finished` push — the terminal outcome for one file. */
  function applyAiOutcome(payload: { fileId: string; batchId?: string; status: AiTerminalStatus }) {
    const item = itemForEvent(payload.fileId, payload.batchId);
    // Already settled: the two pushes that can settle a file (this one and the general
    // record-fields one) both arrive on the happy path, and a later Error push must not undo a
    // file that already reported done — that would turn a created record into a failure in the UI.
    if (!item || isTerminalAi(item.aiStatus)) return;

    // Stamped only for a push that belongs to this queue: another tab's run must not keep the
    // silence watchdog below alive for a batch that really has gone quiet.
    lastAiEventAt = Date.now();

    item.aiStatus = payload.status;
    if (payload.status === "failed") item.errorMessage ??= $t("fileExplorer.upload.failedReasonAi");
    stopTickerWhenIdle();
  }

  /**
   * Settles a file by its own Files-module record instead of its file id. The general
   * record-fields-changed push carries only that record and its new status, and it is a second,
   * independent way for a terminal state to arrive: the classification-specific push can be dropped
   * by the event hub when its buffer is full, and a backend older than #3146 never sends it at all.
   * Without this the counter stays at 0/N while the bar already shows every step as done, and a file
   * that ended on "no template found" leaves the popup stuck mid-way forever.
   */
  function applyAiOutcomeByRecord(payload: { fileRecordId: string; status: AiTerminalStatus }) {
    const item = items.value.find((entry) => entry.fileRecordId === payload.fileRecordId);
    if (!item || isTerminalAi(item.aiStatus)) return;

    lastAiEventAt = Date.now();

    item.aiStatus = payload.status;
    if (payload.status === "failed") item.errorMessage ??= $t("fileExplorer.upload.failedReasonAi");
    stopTickerWhenIdle();
  }

  function reset() {
    stopTicker();
    items.value = [];
    runningBatches.value = [];
    modes.value = {};
  }

  return {
    items,
    modes,
    isRunning,
    uploadTotal,
    uploadedCount,
    failedCount,
    settledCount,
    bytesTotal,
    bytesUploaded,
    uploadPercentage,
    isUploadFinished,
    isBatchSettled,
    hasActiveUpload,
    aiTotal,
    aiProcessedCount,
    aiPercentage,
    isAiFinished,
    hasActiveAi,
    enqueue,
    modeOf,
    run,
    applyAiProgress,
    applyAiOutcome,
    applyAiOutcomeByRecord,
    reset,
  };
});
